import { useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router';

import { useAppDispatch } from 'dux/app/hooks';

import { Text, theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import Spacer from 'Components/Spacer';
import { TextField } from 'Components/TextField';

import { consultationEntryModuleContent } from 'assets/content/homeV2';

import useResponsiveVariant from 'utils/useResponsiveVariant';
import { isValidName } from 'utils/validators';

import { trackHeapEvent } from 'dux/tracking/actions';

const Section = styled.div`
  display: flex;
  justify-content: center;

  width: 100%;
  margin-top: 0;
  padding: 32px 0;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-top: 99px;
    padding: 0 16px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding: 0 32px;
  }
`;

const DesktopBackgroundCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  height: auto;
  padding: 60px 0;
  border-radius: 0;

  background-color: ${theme.colors.neutral[300]};

  ${legacyTheme.breakpoints.up('sm')} {
    flex-direction: row-reverse;
    height: 433px;
    padding: 0;
    border-radius: ${theme.radius.xs};
  }
`;

const ImageTextContainer = styled.div`
  display: flex;
  flex: 0;
  justify-content: center;

  ${legacyTheme.breakpoints.up('sm')} {
    flex: 1;
    max-width: 48vw;
  }
`;

const TextBlock = styled.div`
  display: flex;
  flex: 0;
  flex-direction: column;
  justify-content: center;

  ${legacyTheme.breakpoints.up('sm')} {
    flex: 1;
    max-width: 46vw;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    padding-left: 5vw;
  }
`;

const ImageContainer = styled.div`
  position: relative;
`;

const StyledTextField = styled(TextField)`
  width: 90vw;
  max-width: 327px !important;
  margin-top: -10px;
`;

const BackgroundImage = styled(Image)<{ issafari: boolean }>`
  width: auto;
  height: 417px;
  margin-top: 0;
  /* Safari has a stipulation where the image is not displayed correctly if the object-fit is not set to cover */
  object-fit: ${({ issafari }) => (issafari ? 'cover' : 'contain')};

  ${legacyTheme.breakpoints.up('sm')} {
    height: 503px;
    margin-top: -63px;
  }
`;

const NameText = styled(Text)<{ issafari: boolean }>`
  position: absolute;
  top: 350px;
  left: 28.5%;

  font-family: ${theme.typography.fontFamily.heading};
  font-size: 22px; /* Custom font size for NameText as it's for matching the bottle designs */
  font-weight: 100;

  ${legacyTheme.breakpoints.up('sm')} {
    top: 360px;
    /* Safari requires a different left position to display the text correctly */
    left: ${({ issafari }) => (issafari ? '27.5%' : '28.5%')};
    font-size: 26px; /* Custom font size for NameText as it's for matching the bottle designs */
  }
`;

const consultationFirstNameKey = 'consultationFirstName';

export const clearConsultationFirstName = () => {
  localStorage.removeItem(consultationFirstNameKey);
};

export const getConsultationFirstName = () => {
  return localStorage.getItem(consultationFirstNameKey);
};

function agentHas(keyword: string) {
  return navigator.userAgent.toLowerCase().search(keyword.toLowerCase()) > -1;
}

function isSafari() {
  return agentHas('Safari') && !agentHas('Chrome') && !agentHas('CriOS');
}

// This is the input field for the first name
const NameTextInput = ({
  buttonDisabled,
  firstName,
  handleBlur,
  handleChange,
  handleFocus,
  textFieldRef,
}: {
  buttonDisabled: boolean;
  firstName: string;
  handleBlur: () => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFocus: () => void;
  textFieldRef: React.RefObject<HTMLDivElement>;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleButtonClick = () => {
    if (firstName && firstName !== consultationEntryModuleContent.inputLabel) {
      localStorage.setItem(consultationFirstNameKey, firstName);
    }
    dispatch(trackHeapEvent('Name - Enter'));
    navigate('/consultation/haircare/my-hair/age');
  };

  const nameIsValid = isValidName(firstName);

  return (
    <StyledTextField
      ref={textFieldRef}
      aria-invalid={!nameIsValid ? 'true' : 'false'}
      aria-required="true"
      autoCorrect={false}
      button={{
        dataClick: 'enter_name',
        dataFrom: 'homepage_embedded_name',
        disabled: buttonDisabled || !nameIsValid,
        minChars: 2,
        onClick: handleButtonClick,
        shown: true,
      }}
      data-testid="consultation-entry-module-input"
      error={!nameIsValid && firstName.length > 1 ? 'Invalid first name format' : ''}
      id="firstName"
      margin={false}
      name="firstName"
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      style={{ paddingTop: 0 }}
      type="text"
      value={firstName}
    />
  );
};

// This is the container for the title and the input field for the first name
const TitleContainer = ({
  buttonDisabled,
  firstName,
  handleBlur,
  handleChange,
  handleFocus,
  isMobile,
}: {
  buttonDisabled: boolean;
  firstName: string;
  handleBlur: () => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFocus: () => void;
  isMobile: boolean;
}) => {
  const textFieldRef = useRef<HTMLDivElement>(null);

  const handleContainerClick = () => {
    if (textFieldRef.current) {
      const input = textFieldRef.current.querySelector('input');
      if (input) {
        input.focus();
      }
    }
  };

  return (
    <TextBlock onClick={handleContainerClick}>
      <Text as="h2" textAlign={isMobile ? 'center' : 'left'} variant="label2regular">
        {consultationEntryModuleContent.subHeadline}
      </Text>
      <Spacer axis="vertical" size={isMobile ? 12 : 16} />
      <Text as="h3" textAlign={isMobile ? 'center' : 'left'} variant="h3">
        {consultationEntryModuleContent.headline}
      </Text>
      {!isMobile && <Spacer axis="vertical" size={24} />}
      {!isMobile && (
        <NameTextInput
          buttonDisabled={buttonDisabled}
          firstName={firstName}
          handleBlur={handleBlur}
          handleChange={handleChange}
          handleFocus={handleFocus}
          textFieldRef={textFieldRef}
        />
      )}
    </TextBlock>
  );
};

// This is the main component for the consultation entry module
export const ConsultationEntryModule = () => {
  const { isMobile } = useResponsiveVariant();
  const [firstName, setFirstName] = useState(consultationEntryModuleContent.inputLabel);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const textFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    clearConsultationFirstName();
    setFirstName(consultationEntryModuleContent.inputLabel);
    setButtonDisabled(true);
  }, []);

  const handleContainerClick = () => {
    if (textFieldRef.current) {
      const input = textFieldRef.current.querySelector('input');
      if (input) {
        input.focus();
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 15);
    setFirstName(value);
    setButtonDisabled(value === consultationEntryModuleContent.inputLabel);
    if (value !== consultationEntryModuleContent.inputLabel) {
      localStorage.setItem(consultationFirstNameKey, value);
    } else {
      clearConsultationFirstName();
    }
  };

  const handleFocus = () => {
    if (firstName === consultationEntryModuleContent.inputLabel) {
      setFirstName('');
      setButtonDisabled(false);
      clearConsultationFirstName();
    }
  };

  const handleBlur = () => {
    if (!firstName) {
      setFirstName(consultationEntryModuleContent.inputLabel);
      setButtonDisabled(true);
      clearConsultationFirstName();
    }
  };

  return (
    <Section data-testid="consultation-entry-module">
      <DesktopBackgroundCardContainer onClick={handleContainerClick}>
        <TitleContainer
          buttonDisabled={buttonDisabled}
          firstName={firstName}
          handleBlur={handleBlur}
          handleChange={handleChange}
          handleFocus={handleFocus}
          isMobile={isMobile}
        />
        <ImageTextContainer>
          <ImageContainer>
            <BackgroundImage
              alt="Prose Shampoo Bottle without a name"
              issafari={isSafari()}
              src={consultationEntryModuleContent.img}
            />
            <NameText
              as="h3"
              data-testid="consultation-entry-module-name"
              issafari={isSafari()}
              variant="h3"
            >
              {firstName !== consultationEntryModuleContent.inputLabel ? firstName : ''}
            </NameText>
          </ImageContainer>
        </ImageTextContainer>
        {isMobile && (
          <NameTextInput
            buttonDisabled={buttonDisabled}
            firstName={firstName}
            handleBlur={handleBlur}
            handleChange={handleChange}
            handleFocus={handleFocus}
            textFieldRef={textFieldRef}
          />
        )}
      </DesktopBackgroundCardContainer>
    </Section>
  );
};
